import React, { useMemo } from 'react';
import {
  ClipboardDocumentCheckIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon
} from '@heroicons/react/24/outline';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import LoadingSpinner from '../../../components/common/LoadingSpinner';
import { useValidationsSummary } from '../../../hooks/useValidationsData';

const ValidationOverviewDashboard = ({ connectionId, onTableSelect }) => {
  // Use the hook for data fetching with automatic caching and deduplication
  const { data: summaryData, isLoading: loading, error } = useValidationsSummary(connectionId, {
    enabled: !!connectionId
  });

  // Process table-level health data using useMemo for performance
  const tableHealthData = useMemo(() => {
    if (!summaryData?.validations_by_table) {
      return [];
    }

    return Object.entries(summaryData.validations_by_table).map(([tableName, tableData]) => {
      const total = tableData.total || 0;
      const passing = tableData.passing || 0;
      const failing = tableData.failing || 0;
      const totalKnown = passing + failing;
      const healthScore = totalKnown > 0 ? Math.round((passing / totalKnown) * 100) : 0;

      return {
        table_name: tableName,
        total_validations: total,
        passing,
        failing,
        health_score: healthScore
      };
    }).sort((a, b) => a.table_name.localeCompare(b.table_name)); // Sort alphabetically by table name
  }, [summaryData]);

  // Calculate overall health score (only count validations that have run)
  const calculateOverallHealth = () => {
    if (!summaryData) return 0;
    const totalKnown = summaryData.passing_count + summaryData.failing_count;
    return totalKnown > 0 ? Math.round((summaryData.passing_count / totalKnown) * 100) : 0;
  };

  // Get health status info
  const getHealthStatus = (score) => {
    if (score >= 80) {
      return {
        status: 'Excellent',
        color: 'accent',
        bgColor: 'bg-accent-50',
        textColor: 'text-accent-600',
        borderColor: 'border-accent-200'
      };
    } else if (score >= 60) {
      return {
        status: 'Good',
        color: 'warning',
        bgColor: 'bg-warning-50',
        textColor: 'text-warning-600',
        borderColor: 'border-warning-200'
      };
    } else {
      return {
        status: 'Needs Attention',
        color: 'danger',
        bgColor: 'bg-danger-50',
        textColor: 'text-danger-600',
        borderColor: 'border-danger-200'
      };
    }
  };

  // Custom tooltip for table health chart
  const TableHealthTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-3 border border-secondary-200 shadow-lg rounded-md">
          <p className="font-medium text-secondary-900">{label}</p>
          <p className="text-sm text-secondary-600">Health Score: {data.health_score}%</p>
          <p className="text-sm text-accent-600">Passing: {data.passing}</p>
          <p className="text-sm text-danger-600">Failing: {data.failing}</p>
          <p className="text-sm text-secondary-500">Total: {data.total_validations}</p>
        </div>
      );
    }
    return null;
  };

  if (!connectionId) {
    return (
      <div className="text-center py-12">
        <ClipboardDocumentCheckIcon className="mx-auto h-12 w-12 text-secondary-400" />
        <h3 className="mt-2 text-sm font-medium text-secondary-900">No Connection Selected</h3>
        <p className="mt-1 text-sm text-secondary-500">Select a connection to view validation overview</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner message="Loading validation overview..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-danger-400" />
        <h3 className="mt-2 text-sm font-medium text-secondary-900">Error Loading Data</h3>
        <p className="mt-1 text-sm text-secondary-500">{error.message || 'Failed to load validation overview'}</p>
      </div>
    );
  }

  if (!summaryData || summaryData.total_count === 0) {
    return (
      <div className="text-center py-12">
        <ClipboardDocumentCheckIcon className="mx-auto h-12 w-12 text-secondary-400" />
        <h3 className="mt-2 text-sm font-medium text-secondary-900">No Validations Found</h3>
        <p className="mt-1 text-sm text-secondary-500">No validation data available for this connection</p>
      </div>
    );
  }

  const overallHealth = calculateOverallHealth();
  const healthStatus = getHealthStatus(overallHealth);

  return (
    <div className="space-y-6">
      {/* Overall Health Score */}
      <div className={`rounded-lg border-2 ${healthStatus.borderColor} ${healthStatus.bgColor} p-6`}>
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium text-secondary-900">Overall Data Quality</h3>
            <p className="text-sm text-secondary-600">
              {summaryData.passing_count} of {summaryData.passing_count + summaryData.failing_count} validations passing ({summaryData.unknown_count} not yet run)
            </p>
          </div>
          <div className="text-right">
            <div className={`text-3xl font-bold ${healthStatus.textColor}`}>
              {overallHealth}%
            </div>
            <div className={`text-sm ${healthStatus.textColor}`}>
              {healthStatus.status}
            </div>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white border border-secondary-200 rounded-lg p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <ClipboardDocumentCheckIcon className="h-8 w-8 text-primary-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-secondary-600">Total Validations</p>
              <p className="text-2xl font-semibold text-secondary-900">{summaryData.total_count}</p>
            </div>
          </div>
        </div>

        <div className="bg-white border border-secondary-200 rounded-lg p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <CheckCircleIcon className="h-8 w-8 text-accent-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-secondary-600">Passing</p>
              <p className="text-2xl font-semibold text-accent-600">{summaryData.passing_count}</p>
            </div>
          </div>
        </div>

        <div className="bg-white border border-secondary-200 rounded-lg p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <XCircleIcon className="h-8 w-8 text-danger-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-secondary-600">Failing</p>
              <p className="text-2xl font-semibold text-danger-600">{summaryData.failing_count}</p>
            </div>
          </div>
        </div>

        <div className="bg-white border border-secondary-200 rounded-lg p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <ClockIcon className="h-8 w-8 text-secondary-500" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-secondary-600">Not Yet Run</p>
              <p className="text-2xl font-semibold text-secondary-700">{summaryData.unknown_count}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Table Health Chart */}
      {tableHealthData.length > 0 && (
        <div className="bg-white border border-secondary-200 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-secondary-900">Table Health Scores</h3>
            <p className="text-sm text-secondary-600">
              {tableHealthData.length} tables • Sorted alphabetically • Click to view details
            </p>
          </div>
          {/* Horizontally scrollable container */}
          <div className="overflow-x-auto">
            <div style={{ minWidth: `${Math.max(800, tableHealthData.length * 60)}px` }} className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={tableHealthData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="table_name"
                    tick={{ fontSize: 11 }}
                    angle={-45}
                    textAnchor="end"
                    height={80}
                    interval={0}
                  />
                  <YAxis
                    domain={[0, 100]}
                    tick={{ fontSize: 12 }}
                    label={{ value: 'Health Score (%)', angle: -90, position: 'insideLeft', style: { fontSize: 12 } }}
                  />
                  <Tooltip content={<TableHealthTooltip />} />
                  <Bar
                    dataKey="health_score"
                    fill="#10B981"
                    cursor="pointer"
                    onClick={(data) => {
                      if (onTableSelect && data.table_name) {
                        onTableSelect(data.table_name);
                      }
                    }}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ValidationOverviewDashboard;